import { useEffect, useState } from 'react';

import classNames from '@/utils/classnames';

import { useReducedMotion } from '../../hooks/useReducedMotion';

import { AgentMark } from './AgentMark';

/**
 * Streaming placeholder shown while Zooid Atlas is working but hasn't produced text or tool calls
 * yet. The agent hexagon glyph (spec §6) breathes with the Pulse (§9) and the label cycles a
 * playful word list. Honors `prefers-reduced-motion`: the glow holds static (via CSS) and the word
 * stops cycling.
 */

const WORDS = ['Thinking', 'Manifesting', 'Conjuring', 'Reticulating', 'Pondering', 'Scheming', 'Percolating'];
const CYCLE_MS = 2200;

/**
 * The cycling word, and whether it may animate.
 *
 * Shared by the empty-turn placeholder and {@link LiveActivityLine} so a turn that has already
 * produced tool calls keeps the same vocabulary and cadence as one that hasn't — the run reads as
 * one continuous thing rather than two different loaders.
 */
function useCyclingWord() {
  const reducedMotion = useReducedMotion();
  // Pick a random starting word so the loader feels alive across turns rather than always opening
  // on "Thinking".
  const [index, setIndex] = useState(() => Math.floor(Math.random() * WORDS.length));

  useEffect(() => {
    if (reducedMotion) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % WORDS.length), CYCLE_MS);
    return () => clearInterval(id);
  }, [reducedMotion]);

  return { word: WORDS[index]!, index, reducedMotion };
}

/** `label` names a concrete state (a retry, a running tool) in place of the cycling word. */
export function ThinkingIndicator({ label }: { label?: string } = {}) {
  const { word, index, reducedMotion } = useCyclingWord();

  return (
    <div className="flex items-center gap-2" aria-live="polite">
      <AgentMark pulsing />
      <span
        key={label ?? (reducedMotion ? 'static' : index)}
        className={classNames(
          'text-xs font-medium text-fg-secondary',
          !label && !reducedMotion && 'animate-word-cycle',
        )}
      >
        {label ?? `${word}…`}
      </span>
    </div>
  );
}

/**
 * The same "still working" signal, for a turn that is already past its first output.
 *
 * {@link ThinkingIndicator} is replaced by the Atlas header the moment a tool call or a token
 * lands, and a long tool-heavy run then had no live affordance at all beyond a 12px spinner on a
 * header the user may well have collapsed — the thread simply looked dead between rounds. This
 * carries the signal for the rest of the turn, sitting in the turn's content column rather than
 * repeating the hexagon the header already shows.
 *
 * `label` overrides the cycling word when there is something concrete to report — the tool that is
 * running, or a retry in progress — since naming it beats a generic loader.
 */
export function LiveActivityLine({ label }: { label?: string }) {
  const { word, index, reducedMotion } = useCyclingWord();
  const text = label ?? `${word}…`;

  return (
    <div className="flex items-center gap-2" aria-live="polite">
      <span className="zooid-pulse size-1.5 shrink-0 rounded-full bg-brand-text" />
      <span
        key={label ?? (reducedMotion ? 'static' : index)}
        className={classNames('text-xs font-medium text-fg-tertiary', !label && !reducedMotion && 'animate-word-cycle')}
      >
        {text}
      </span>
    </div>
  );
}
